import { useState, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  useDisableTotpMutation,
  useEnableTotpMutation,
  useMeQuery,
  useSetupTotpMutation,
  type TotpSetupResponse,
} from '@shared/api';
import { Card, CardTitle, CardSubtitle } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Input, Label, Field } from '@shared/ui/Input';
import { Badge } from '@shared/ui/Badge';

const Page = styled.div`
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const H1 = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const QrFrame = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing(4)};
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing(3)} 0;

  img {
    width: 220px;
    height: 220px;
    image-rendering: pixelated;
  }
`;

const Secret = styled.code`
  display: block;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-family: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
  font-size: 12.5px;
  word-break: break-all;
  user-select: all;
`;

const ErrorBox = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  background: ${({ theme }) => theme.colors.status.danger}15;
  border: 1px solid ${({ theme }) => theme.colors.status.danger}33;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Para = styled.p`
  margin: ${({ theme }) => theme.spacing(2)} 0;
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export function SecurityPage() {
  const { data: me } = useMeQuery();
  const totpEnabled = me?.totp_enabled ?? false;

  const [setupTotp, { isLoading: isSettingUp }] = useSetupTotpMutation();
  const [enableTotp, { isLoading: isEnabling }] = useEnableTotpMutation();
  const [disableTotp, { isLoading: isDisabling }] = useDisableTotpMutation();

  const [setupData, setSetupData] = useState<TotpSetupResponse | null>(null);
  const [enableCode, setEnableCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onStartSetup() {
    setError(null);
    try {
      const res = await setupTotp().unwrap();
      setSetupData(res);
      setEnableCode('');
    } catch (err) {
      setError(extractMsg(err, 'Не удалось начать настройку 2FA'));
    }
  }

  async function onConfirmEnable(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await enableTotp({ code: enableCode }).unwrap();
      setSetupData(null);
      setEnableCode('');
    } catch (err) {
      setError(extractMsg(err, 'Неверный код'));
    }
  }

  async function onDisable(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await disableTotp({
        password: disablePassword,
        code: disableCode,
      }).unwrap();
      setDisablePassword('');
      setDisableCode('');
    } catch (err) {
      setError(extractMsg(err, 'Не удалось отключить 2FA'));
    }
  }

  return (
    <Page>
      <Header>
        <H1>Безопасность аккаунта</H1>
        <CardSubtitle>
          Двухфакторная аутентификация защищает вход даже если пароль попал в чужие руки.
        </CardSubtitle>
      </Header>

      <Card>
        <CardTitle>Двухфакторная аутентификация (2FA)</CardTitle>
        <StatusRow>
          <span style={{ fontSize: 13 }}>Текущий статус:</span>
          {totpEnabled ? (
            <Badge $tone="success">включена</Badge>
          ) : (
            <Badge $tone="warning">не настроена</Badge>
          )}
        </StatusRow>

        {!totpEnabled && !setupData && (
          <>
            <Para>
              Поддерживается любое authenticator-приложение: <b>Яндекс Ключ</b>,
              Google Authenticator, Authy, 1Password, Bitwarden, Microsoft Authenticator.
            </Para>
            <Para>
              При входе после ввода логина и пароля у вас попросят 6-значный код,
              который обновляется каждые 30 секунд в приложении.
            </Para>
            <Actions>
              <Button onClick={onStartSetup} disabled={isSettingUp}>
                {isSettingUp ? 'Подготовка…' : 'Настроить 2FA'}
              </Button>
            </Actions>
          </>
        )}

        {!totpEnabled && setupData && (
          <>
            <Para>
              <b>1.</b> Откройте приложение-аутентификатор и отсканируйте QR-код.
              Либо вставьте секретный ключ вручную.
            </Para>
            <QrFrame>
              <img
                src={`data:image/png;base64,${setupData.qr_png_base64}`}
                alt="QR-код для добавления аккаунта в authenticator"
              />
            </QrFrame>
            <Para style={{ fontSize: 12.5 }}>Ручной ввод секретного ключа:</Para>
            <Secret>{setupData.secret}</Secret>
            <Para>
              <b>2.</b> Введите код, который показывает приложение, чтобы подтвердить настройку.
            </Para>
            <form onSubmit={onConfirmEnable}>
              <Field>
                <Label htmlFor="enable-code">Код из приложения</Label>
                <Input
                  id="enable-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={enableCode}
                  onChange={(e) =>
                    setEnableCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="123456"
                  maxLength={6}
                  pattern="\d{6}"
                  required
                  autoFocus
                  style={{ letterSpacing: '0.4em', textAlign: 'center', fontSize: 18 }}
                />
              </Field>
              <Actions>
                <Button type="submit" disabled={isEnabling || enableCode.length !== 6}>
                  {isEnabling ? 'Включаем…' : 'Включить 2FA'}
                </Button>
                <Button
                  type="button"
                  $variant="ghost"
                  onClick={() => {
                    setSetupData(null);
                    setEnableCode('');
                    setError(null);
                  }}
                >
                  Отмена
                </Button>
              </Actions>
            </form>
          </>
        )}

        {totpEnabled && (
          <>
            <Para>
              2FA уже активна. Чтобы отключить — введите текущий пароль и код из приложения.
            </Para>
            <form onSubmit={onDisable}>
              <Field>
                <Label htmlFor="disable-password">Пароль</Label>
                <Input
                  id="disable-password"
                  type="password"
                  autoComplete="current-password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="disable-code">Код из приложения</Label>
                <Input
                  id="disable-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={disableCode}
                  onChange={(e) =>
                    setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="123456"
                  maxLength={6}
                  pattern="\d{6}"
                  required
                  style={{ letterSpacing: '0.4em', textAlign: 'center', fontSize: 18 }}
                />
              </Field>
              <Actions>
                <Button
                  type="submit"
                  $variant="danger"
                  disabled={isDisabling || !disablePassword || disableCode.length !== 6}
                >
                  {isDisabling ? 'Отключаем…' : 'Отключить 2FA'}
                </Button>
              </Actions>
            </form>
          </>
        )}

        {error && <ErrorBox>{error}</ErrorBox>}
      </Card>
    </Page>
  );
}

function extractMsg(err: unknown, fallback: string): string {
  const m = (err as { data?: { error?: { message?: string } } })?.data?.error?.message;
  return m ?? fallback;
}
